import { CauseId } from "#shared/errors"
import { X_ERROR_CAUSE_ID } from "#shared/headers";

export interface ApiError extends Error {
  response?: {
    headers?: Headers
  };
  statusCode?: number;
}

export interface ErrorProblem {
  problem: string
  solution?: string
}

const createErrorTranslator = () => {
  const SOLUTION_RETRY = 'Please try again.'
  const SOLUTION_RETRY_LATER = 'Please try again later.'
  const SOLUTION_CLEAR_BROWSER_CACHE = "Please clear your browser's cache and try again."
  const SOLUTION_LOGIN = "Please log in with your credentials."
  const SOLUTION_NETWORK = "Please check your network connection and try again."
  const SOLUTION_ENTER_CORRECTLY = "Please check that you have entered it correctly."
  const SOLUTION_PROXY = "Please check your network proxy settings and try again."

  /**
   * Errors reported by the server: hexadecimal representation is prefixed with B
   * Errors reported by the client: hexadecimal representation is prefixed with F
   * Data validation failures: hexadecimal representations is prefixed with D
   */

  // Map of numeric error cause IDs to their EN descriptions
  const causeMap: Record<CauseId, ErrorProblem> = {
    // Access errors
    [CauseId.ACCESS_DENIED]: { problem: 'You do not have access.' },
    [CauseId.NOT_LOGGED_IN]: { problem: 'You are not authorized to perform this action.', solution: SOLUTION_LOGIN },
    [CauseId.EMAIL_EXISTS]: { problem: 'This email address is already assigned to another account.', solution: 'Check if you entered your email address correctly. If you belief your identity was stolen, contact your administrator.' },
    [CauseId.ID_NUMBER_EXISTS]: { problem: 'This South African ID is already assigned to another account.', solution: 'Check if you entered your ID number correctly. If you belief your identity was stolen, contact your administrator.' },
    [CauseId.PASSPORT_EXISTS]: { problem: 'This passport number is already assigned to another account.', solution: 'Check if you entered your passport number correctly. If you belief your identity was stolen, contact your administrator.' },
    [CauseId.LOGIN_FAILED]: { problem: 'Your login attempt failed.', solution: SOLUTION_CLEAR_BROWSER_CACHE },
    [CauseId.SESSION_EXPIRED]: { problem: 'Your session has expired.', solution: SOLUTION_LOGIN },
    [CauseId.PASSWORD_WRONG]: { problem: 'You have provided the wrong password.', solution: SOLUTION_LOGIN },
    [CauseId.PHONE_EXISTS]: { problem: 'This phone number is already assigned to another account.', solution: 'Check if you entered your phone number correctly. If you belief your identity was stolen, contact your administrator.' },
    [CauseId.ACCOUNT_LOCKED]: { problem: 'Your account was locked.', solution: 'You will not be able to login until your account was unlocked.' },

    // General errors
    [CauseId.CONNECTION_FAILED]: { problem: 'Failed to connect to the server.', solution: SOLUTION_NETWORK },
    [CauseId.UNEXPECTED_RESPONSE]: { problem: 'An unexpected response received from the server.', solution: SOLUTION_PROXY },
    [CauseId.INVALID_REQUEST]: { problem: 'Your browser sent an invalid request.', solution: SOLUTION_CLEAR_BROWSER_CACHE },
    [CauseId.NOT_FOUND]: { problem: 'The data cannot be found.' },
    [CauseId.SERVER_ERROR]: { problem: 'Our server failed to process your request.' },
    [CauseId.SERVICE_UNAVAILABLE]: { problem: 'Our server is temporarily unavailable.', solution: SOLUTION_RETRY_LATER },
    [CauseId.UNEXPECTED_ERROR]: { problem: 'An unexpected error occurred.' },

    // Data issues
    [CauseId.INVALID_CREDENTIALS]: { problem: 'You have specified the wrong login credentials.' },
    [CauseId.INVALID_ACCOUNT_ID]: { problem: 'You provided an invalid account number.' },
    [CauseId.INVALID_PHONE]: { problem: 'You provided an invalid phone number.', solution: 'Only alpha/numeric characters allowed.' },
    [CauseId.INVALID_EMAIL]: { problem: 'You provided an invalid e-mail address.', solution: SOLUTION_ENTER_CORRECTLY },
    [CauseId.INVALID_ID_NUMBER]: { problem: 'You provided an invalid South African ID.', solution: SOLUTION_ENTER_CORRECTLY },
    [CauseId.INVALID_PASSPORT]: { problem: 'You provided an invalid passport number.', solution: SOLUTION_ENTER_CORRECTLY },
    [CauseId.INVALID_PASSWORD]: { problem: 'You provided an invalid password.', solution: 'Passwords may only contain alpha/numeric and certain special characters like spaces or !@#$%^&*()-=_+/?[]{}~<>.,' },
    [CauseId.PASSWORD_TOO_SHORT]: { problem: 'Your password need to be at least 8 characters long.', solution: SOLUTION_RETRY },
    [CauseId.PASSWORD_REQUIRED]: { problem: 'You did not provide a password for the new account.', solution: SOLUTION_RETRY },
    [CauseId.INVALID_DATA]: { problem: 'Invalid data rejected by the server.' },
    [CauseId.USER_NOT_FOUND]: { problem: 'User not found.' },
    [CauseId.DUPLICATE_ENTRY]: { problem: 'Duplicate entry.' },
    [CauseId.VALIDATION_ERROR]: { problem: 'Invalid data validation error.' },
    [CauseId.CONFIGURATION_ERROR]: { problem: 'Server configuration error.', solution: SOLUTION_RETRY_LATER },
    [CauseId.EXTERNAL_SERVICE_ERROR]: { problem: 'External service error.', solution: SOLUTION_RETRY_LATER },
  }

  const UNKNOWN = causeMap[CauseId.UNEXPECTED_ERROR]

  const getCauseId = (errorId: number): CauseId => {
    if (Object.values(CauseId).includes(errorId as CauseId)) {
      return errorId as CauseId
    } else {
      return CauseId.UNEXPECTED_ERROR;
    }
  }

  /**
   * Translates a numeric cause ID to its human-readable message
   */
  const translateCauseId = (errorId: number): ErrorProblem => {
    return causeMap[getCauseId(errorId)] || UNKNOWN
  }

  const extractCauseIdNumber = (error?: ApiError): number | null => {
    if (error?.response?.headers) {
      const causeId = error?.response?.headers?.get(X_ERROR_CAUSE_ID)
      try {
        if (causeId) {
          return parseInt(causeId)
        }
      } catch (error) {
        console.error("Invalid response: " + error)
      }
    }
    return null
  }

  const getGeneralCauseId = (statusCode?: number): CauseId => {
    if (!statusCode) {
      return CauseId.CONNECTION_FAILED
    } else {
      switch (statusCode) {
        case 400: return CauseId.INVALID_REQUEST
        case 401: return CauseId.NOT_LOGGED_IN
        case 403: return CauseId.ACCESS_DENIED
        case 404: return CauseId.NOT_FOUND
        case 422: return CauseId.INVALID_DATA
        case 500: return CauseId.SERVER_ERROR
        case 503: return CauseId.SERVICE_UNAVAILABLE
        default:
          return CauseId.UNEXPECTED_ERROR
      }
    }
  }

  return {
    extractCauseIdNumber,
    getCauseId,
    getGeneralCauseId,
    translateCauseId,
    UNKNOWN
  }
}

// Create and export a default instance
export const errors = createErrorTranslator()
